import { plainQuoteRequestInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { QUOTE_REQUEST_SHARED_FIELD_KEYS, type QuoteRequestSharedFieldKey } from '@/entities/quote';
import { trpc } from '@/shared/api';
import {
  type QuoteRequestDestinationErrors,
  type QuoteRequestFieldErrors,
  type QuoteRequestSubmissionErrors,
  quoteRequestSubmitErrors,
  realClock,
  toQuoteRequestCommonInput,
  type UseSubmitQuoteRequestOptions,
  type UseSubmitQuoteRequestResult,
  validateQuoteRequest,
} from './quote-request-submission';
import type { QuoteRequestFormValues } from './use-submit-quote-request';

/** 일반 견적 폼 입력 — 디자인·인쇄 방식 대신 상품·색상을 직접 받는다. 나머지 칸은 디자인 견적과 같다. */
export type PlainQuoteRequestFormValues = Omit<
  QuoteRequestFormValues,
  'designId' | 'printMethodId'
> & {
  readonly productId: string;
  readonly colorOptionId: string;
};

/** 일반 견적이 보여 주는 공용 칸 — 인쇄 방식 칸이 없어 그 오류는 내지 않는다(오더라도 일반 폼 오류다). */
const PLAIN_FIELD_KEYS: readonly QuoteRequestSharedFieldKey[] =
  QUOTE_REQUEST_SHARED_FIELD_KEYS.filter((key) => key !== 'printMethodId');

const toInput = (values: PlainQuoteRequestFormValues): unknown => ({
  productId: values.productId,
  colorOptionId: values.colorOptionId,
  ...toQuoteRequestCommonInput(values),
});

/**
 * 회원 일반 견적 요청 제출(quotes.submitPlainRequest, 일반 견적 설계 §5-3) — 편집영역 없는 상품을 디자인·인쇄 방식 없이 요청한다.
 * 검증·칸 오류·서버 거절 처리는 디자인 견적(`useSubmitQuoteRequest`)과 같은 공용 모듈을 쓴다.
 */
export const useSubmitPlainQuoteRequest = ({
  minOrderQty,
  priceTiers,
  shippingFeeSettings,
  sizeOptions,
  now = realClock,
}: UseSubmitQuoteRequestOptions): UseSubmitQuoteRequestResult<PlainQuoteRequestFormValues> => {
  const [fieldErrors, setFieldErrors] = useState<QuoteRequestFieldErrors>({});
  const [destinationErrors, setDestinationErrors] = useState<QuoteRequestDestinationErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const mutation = trpc.quotes.submitPlainRequest.useMutation();

  const show = (errors: QuoteRequestSubmissionErrors): void => {
    setFieldErrors(errors.fieldErrors);
    setDestinationErrors(errors.destinationErrors);
    setFormError(errors.formError);
  };

  const submit = async (values: PlainQuoteRequestFormValues): Promise<string | null> => {
    const validation = validateQuoteRequest(
      plainQuoteRequestInputSchema.safeParse(toInput(values)),
      values,
      { minOrderQty, priceTiers, now: now() },
      shippingFeeSettings,
      PLAIN_FIELD_KEYS,
      sizeOptions,
    );
    show(validation);

    if (validation.input === null) {
      return null;
    }

    try {
      const result = await mutation.mutateAsync(validation.input);
      return result.quoteRequestId;
    } catch (error) {
      show(quoteRequestSubmitErrors(error, values, PLAIN_FIELD_KEYS));
      return null;
    }
  };

  return { fieldErrors, destinationErrors, formError, isPending: mutation.isPending, submit };
};
